import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Progress } from '@/components/ui/Progress';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import { LevelPlayground } from '@/features/tutorial/LevelPlayground';
import { Clock, Flag } from 'lucide-react';
import { useGameStore } from '@/stores/gameStore';
import { useUserStore } from '@/stores/userStore';

interface Challenge {
  id: string;
  type: string;
  difficulty: string;
  title: string;
  description: string;
  timeLimit: number;
  starterCode: Record<string, any>;
  requirements: string[];
  scoringCriteria: any;
  hints: string[];
  xpReward: number;
}

interface ChallengeContainerProps {
  challengeData: Challenge;
}

export function ChallengeContainer({ challengeData }: ChallengeContainerProps) {
  const [timeRemaining, setTimeRemaining] = useState(challengeData.timeLimit);
  const [isRunning, setIsRunning] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [score, setScore] = useState(0);

  const { startTimer, pauseTimer } = useGameStore();
  const { addXP, progress } = useUserStore();

  // Timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning && timeRemaining > 0 && !isCompleted) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isRunning, timeRemaining, isCompleted]);

  const handleStart = () => {
    setIsRunning(true);
    startTimer(challengeData.timeLimit);
  };

  const handleTimeUp = () => {
    setShowSubmitModal(true);
  };

  const handleSubmit = () => {
    // Calculate score based on time and other factors
    const timeBonus = Math.floor(
      (timeRemaining / challengeData.timeLimit) * 50
    );
    const calculatedScore =
      challengeData.scoringCriteria.basePoints + timeBonus;

    // Check if challenge was already completed
    const previousProgress = progress.challengeProgress.find(
      (p) => p.challengeId === challengeData.id
    );
    const wasAlreadyCompleted = previousProgress?.completed || false;

    setScore(calculatedScore);
    setIsCompleted(true);
    pauseTimer();

    // Award XP only if not previously completed
    if (!wasAlreadyCompleted) {
      addXP(challengeData.xpReward);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const timePercentage = (timeRemaining / challengeData.timeLimit) * 100;
  const timeColor =
    timePercentage > 50
      ? 'text-success-400'
      : timePercentage > 25
      ? 'text-warning-400'
      : 'text-error-400';

  return (
    <div className="max-w-7xl mx-auto">
      {/* Challenge Header */}
      <Card variant="elevated" className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant={
                    challengeData.difficulty === 'easy'
                      ? 'success'
                      : challengeData.difficulty === 'medium'
                      ? 'warning'
                      : 'error'
                  }
                  size="sm"
                >
                  {challengeData.difficulty}
                </Badge>
                <Badge variant="info" size="sm">
                  {challengeData.type}
                </Badge>
              </div>
              <CardTitle className="text-2xl">{challengeData.title}</CardTitle>
              <p className="text-primary-200 mt-2">
                {challengeData.description}
              </p>
            </div>

            {/* Timer */}
            <div className="text-right">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={20} className={timeColor} />
                <span className={`text-2xl font-bold ${timeColor}`}>
                  {formatTime(timeRemaining)}
                </span>
              </div>
              {!isRunning && !isCompleted && (
                <Button variant="primary" size="sm" onClick={handleStart}>
                  Start Challenge
                </Button>
              )}
              {isRunning && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSubmitModal(true)}
                >
                  Submit Solution
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Progress value={timePercentage} />
        </CardContent>
      </Card>

      {/* Requirements */}
      <Card variant="outline" className="mb-6">
        <CardHeader>
          <CardTitle>Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {challengeData.requirements.map((req, index) => (
              <li key={index} className="flex items-start gap-2 text-primary-200">
                <Flag size={16} className="mt-1 text-primary-500 flex-shrink-0" />
                <span>{req}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Playground */}
      {isRunning && (
        <Card variant="elevated">
          <CardHeader>
            <CardTitle>Code Editor</CardTitle>
          </CardHeader>
          <CardContent>
            <LevelPlayground starterCode={challengeData.starterCode} />
          </CardContent>
        </Card>
      )}

      {/* Submit Modal */}
      <Modal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        title={isCompleted ? 'Challenge Complete!' : 'Submit Solution?'}
        closeOnOverlayClick={false}
      >
        {!isCompleted ? (
          <>
            <p className="text-primary-200 mb-4">
              Are you ready to submit your solution? Your code will be evaluated
              and you'll receive your score.
            </p>
            <p className="text-primary-300 text-sm">
              Time remaining: {formatTime(timeRemaining)}
            </p>
            <ModalFooter>
              <Button
                variant="ghost"
                onClick={() => setShowSubmitModal(false)}
              >
                Keep Working
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                Submit Solution
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <div className="text-center py-4">
              <div className="text-4xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-primary-400 mb-2">
                Challenge Complete!
              </h3>
              <div className="mb-4">
                <div className="text-4xl font-bold text-primary-500 mb-2">
                  {score}
                </div>
                <p className="text-primary-300">Total Score</p>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-dark-100 rounded-lg">
                  <div className="text-sm text-primary-300">Time Bonus</div>
                  <div className="text-lg font-bold text-primary-400">
                    +{Math.floor((timeRemaining / challengeData.timeLimit) * 50)}
                  </div>
                </div>
                <div className="p-3 bg-dark-100 rounded-lg">
                  <div className="text-sm text-primary-300">XP Earned</div>
                  <div className="text-lg font-bold text-success-400">
                    +{challengeData.xpReward}
                  </div>
                </div>
              </div>
            </div>
            <ModalFooter>
              <Button variant="outline" onClick={() => window.location.href = '/challenge'}>
                Back to Challenges
              </Button>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
}
